import { useId, useState, useEffect } from 'react'

interface Props {
  className?: string
  ariaHidden?: boolean
}

/**
 * Inline SVG logo (HeilPort) — colours driven by CSS vars so the ThemePicker
 * can override them live. Per-theme defaults live in theme.css.
 *   --logo-halo      organic crescent behind the disc
 *   --logo-disc-1/-2 the disc gradient (top/bottom)
 *   --logo-cross     the gold tile-cross
 *   --logo-heil      the "Heil" wordmark (falls back to --logo-cross)
 *   --logo-port      the "Port" wordmark (falls back to --logo-cross)
 */
/** Logo wordmark text — stored with the theme (localStorage), editable in the ThemePicker. */
function readLogoText(): { t1: string; t2: string } {
  try {
    const raw = localStorage.getItem('mashfa-theme')
    const o = raw ? JSON.parse(raw) : null
    if (o?.logoText) return { t1: o.logoText.t1 ?? 'HEIL', t2: o.logoText.t2 ?? 'PORT' }
  } catch { /* ignore */ }
  return { t1: 'HEIL', t2: 'PORT' }
}

export default function MashfaLogo({ className = '', ariaHidden = true }: Props) {
  const discId = useId()
  const [text, setText] = useState(readLogoText)
  useEffect(() => {
    const onChange = () => setText(readLogoText())
    window.addEventListener('mashfa-logo-text', onChange)
    window.addEventListener('storage', onChange)
    return () => {
      window.removeEventListener('mashfa-logo-text', onChange)
      window.removeEventListener('storage', onChange)
    }
  }, [])
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 220 220"
      aria-hidden={ariaHidden}
    >
      <defs>
        <linearGradient id={discId} x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor="var(--logo-disc-1, #3a3026)" />
          <stop offset="100%" stopColor="var(--logo-disc-2, #1c160f)" />
        </linearGradient>
      </defs>
      <g transform="translate(110, 110)">
        {/* Halo crescent — peeks out top-left, hidden behind the disc elsewhere */}
        <circle cx="-22" cy="8" r="85" fill="var(--logo-halo, #e8d7a8)" />
        {/* Dark disc */}
        <circle cx="0" cy="0" r="80" fill={`url(#${discId})`} />
        {/* Gold tile-cross (centre + 4 arms, thin gaps) */}
        <g fill="var(--logo-cross, #c9b364)">
          <rect x="-5.5" y="-29.5" width="11" height="11" rx="2.5" />
          <rect x="-18.5" y="-16.5" width="11" height="11" rx="2.5" />
          <rect x="-5.5" y="-16.5" width="11" height="11" rx="2.5" />
          <rect x="7.5" y="-16.5" width="11" height="11" rx="2.5" />
          <rect x="-5.5" y="-3.5" width="11" height="11" rx="2.5" />
        </g>
        {/* Wordmark — "Heil" + "Port" separately colourable */}
        <text
          x="0"
          y="34"
          textAnchor="middle"
          fontFamily="Instrument Serif, Georgia, serif"
          fontStyle="italic"
          fontWeight="400"
          fontSize="23"
          letterSpacing="0.5"
        >
          <tspan fill="var(--logo-heil, var(--logo-cross, #c9b364))">{text.t1}</tspan><tspan fill="var(--logo-port, var(--logo-cross, #c9b364))">{text.t2}</tspan>
        </text>
      </g>
    </svg>
  )
}
